import { useState, useMemo } from "react";
import { AppVulnerabilitySummaryCards } from "./AppVulnerabilitySummaryCards";
import { AppVulnerabilityFilters } from "./AppVulnerabilityFilters";
import { AppVulnerabilityTable } from "./AppVulnerabilityTable";
import { AppVulnerabilityDetailPanel } from "./AppVulnerabilityDetailPanel";
import { AppVulnerability, AppVulnerabilitySummary } from "@/types/appVulnerability";
import { useAppVulnerabilities } from "@/hooks/useAppVulnerabilities";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

export function AppVulnerabilityDashboard() {
  const [timeRange, setTimeRange] = useState("all");
  const [appName, setAppName] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVulnerability, setSelectedVulnerability] = useState<AppVulnerability | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);

  const { vulnerabilities, summary, isLoading, triggerScan, isScanning } = useAppVulnerabilities();

  console.log('[AppVulnerabilityDashboard] Hook values:', {
    isLoading,
    isScanning,
    hasTriggerScan: typeof triggerScan === 'function',
    vulnerabilitiesCount: vulnerabilities?.length || 0,
  });

  // Calculate summary statistics
  const calculatedSummary: AppVulnerabilitySummary = useMemo(() => {
    if (summary) return summary;

    const critical = vulnerabilities.filter(v => v.severity === 'CRITICAL').length;
    const high = vulnerabilities.filter(v => v.severity === 'HIGH').length;
    const medium = vulnerabilities.filter(v => v.severity === 'MEDIUM').length;
    const low = vulnerabilities.filter(v => v.severity === 'LOW').length;
    const actively_exploited_count = vulnerabilities.filter(v => v.actively_exploited).length;

    const appMap = new Map<string, { count: number; highest_severity: string }>();
    vulnerabilities.forEach(v => {
      const existing = appMap.get(v.app_name);
      if (existing) {
        existing.count++;
      } else {
        appMap.set(v.app_name, { count: 1, highest_severity: v.severity });
      }
    });

    const top_vulnerable_apps = Array.from(appMap.entries())
      .map(([app_name, data]) => ({ 
        app_name, 
        vulnerability_count: data.count,
        highest_severity: data.highest_severity 
      }))
      .sort((a, b) => b.vulnerability_count - a.vulnerability_count)
      .slice(0, 5);

    const affected_devices_count = vulnerabilities.reduce((sum, v) => sum + v.affected_devices_count, 0);

    return {
      total: vulnerabilities.length,
      critical,
      high,
      medium,
      low,
      actively_exploited_count,
      actively_exploited_percentage: vulnerabilities.length > 0 
        ? (actively_exploited_count / vulnerabilities.length) * 100 
        : 0,
      affected_apps_count: appMap.size,
      affected_devices_count,
      top_vulnerable_apps,
    };
  }, [vulnerabilities, summary]);

  // Filter vulnerabilities
  const filteredVulnerabilities = useMemo(() => {
    return vulnerabilities.filter(vuln => {
      // Time range filter
      if (timeRange !== 'all') {
        const publishedDate = new Date(vuln.published_date);
        const now = new Date();
        const daysDiff = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24);

        if (timeRange === '24h' && daysDiff > 1) return false;
        if (timeRange === '7d' && daysDiff > 7) return false;
        if (timeRange === '30d' && daysDiff > 30) return false;
        if (timeRange === '90d' && daysDiff > 90) return false;
      }

      // App filter
      if (appName !== 'all' && !vuln.app_name.toLowerCase().includes(appName.toLowerCase())) {
        return false;
      }

      // Severity filter
      if (severity !== 'all' && vuln.severity !== severity) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return vuln.cve_id.toLowerCase().includes(query) ||
          vuln.app_name.toLowerCase().includes(query) ||
          (vuln.description && vuln.description.toLowerCase().includes(query));
      }

      return true;
    });
  }, [vulnerabilities, timeRange, appName, severity, searchQuery]);

  const handleRowClick = (vulnerability: AppVulnerability) => {
    setSelectedVulnerability(vulnerability);
    setDetailPanelOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Progress value={40} className="w-56 mb-2" />
        <p className="text-sm text-muted-foreground">Loading application vulnerabilities...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-row justify-between items-center gap-4 mb-4">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">
            Application vulnerabilities detected across your device inventory
          </p>
        </div>
        <div className="flex-shrink-0">
          <Button
            onClick={() => {
              console.log('[Scan Button] Clicked, isScanning:', isScanning);
              if (triggerScan) {
                triggerScan();
              } else {
                console.error('[Scan Button] triggerScan is undefined!');
              }
            }}
            disabled={isScanning}
            variant="default"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isScanning ? 'animate-spin' : ''}`} />
            {isScanning ? 'Scanning...' : 'Scan Now'}
          </Button>
        </div>
      </div>

      <AppVulnerabilitySummaryCards summary={calculatedSummary} vulnerabilities={vulnerabilities} />
      
      <div className="backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl p-6 shadow-md transition-all duration-300">
        <AppVulnerabilityFilters
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          appName={appName}
          onAppNameChange={setAppName}
          severity={severity}
          onSeverityChange={setSeverity}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </div>

      <AppVulnerabilityTable
        vulnerabilities={filteredVulnerabilities}
        onRowClick={handleRowClick}
      />

      <AppVulnerabilityDetailPanel
        vulnerability={selectedVulnerability}
        open={detailPanelOpen}
        onOpenChange={setDetailPanelOpen}
      />
    </div>
  );
}
